/*
  Calendar view of the Tasks page (screen 12-tasks, idea 6) — the same tasks and filters as the board.
    • Week (default): Mon–Sun, narrower weekend columns (full width when the day is today or has tasks), today
      marked; multi-day tasks (start_date → due_date)
      are bars across their days, other tasks sit on their due day.
    • Month: 6-week grid, up to 3 tasks a day + "+N more" (opens that week).
    • Drag a task to another day to change its due date (a multi-day task keeps its length). The keyboard /
      touch way is the task pane's Due row (click the task).
    • Footer: overdue tasks from before this week, tasks without a date, and what is due next week.
  Phones get the week as an agenda (one day under the other).
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): nothing drags; a task only opens.
*/
import { useMemo, useState } from 'react';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, MoveHorizontal, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import { StatusIcon } from './TaskCard';
import {
  addDaysIso,
  daysBetweenIso,
  isClosed,
  isoDate,
  monthGrid,
  startOfWeekIso,
  taskSpan,
  todayIso,
  weekDays,
  weekdayIndex,
} from './taskModel';

const WEEKDAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DRAG_TYPE = 'application/x-sara-task';

/**
 * Week columns: weekend days are narrower (0.62fr) — unless the day is today or has tasks, which then get
 * the full width so their task names stay readable.
 */
export const weekColumnTemplate = (days, { today = '', busyDays = new Set() } = {}) => days
  .map((day) => (weekdayIndex(day) >= 5 && day !== today && !busyDays.has(day) ? 'minmax(0,0.62fr)' : 'minmax(0,1fr)'))
  .join(' ');

const dayNumber = (iso) => Number(String(iso).slice(8, 10));

/** Multi-day bars of a week, stacked in rows so they never overlap. */
const layoutBars = (items, days) => {
  const first = days[0];
  const last = days[days.length - 1];
  const rows = [];
  return items
    .filter(({ span }) => span.multi && span.start <= last && span.end >= first)
    .sort((a, b) => (a.span.start < b.span.start ? -1 : a.span.start > b.span.start ? 1 : 0))
    .map((item) => {
      const startCol = Math.max(0, daysBetweenIso(first, item.span.start));
      const endCol = Math.min(days.length - 1, daysBetweenIso(first, item.span.end));
      let row = rows.findIndex((end) => end < startCol);
      if (row === -1) {
        row = rows.length;
        rows.push(endCol);
      } else {
        rows[row] = endCol;
      }
      return { ...item, startCol, endCol, row, clippedStart: item.span.start < first, clippedEnd: item.span.end > last };
    });
};

function TaskChip({ item, today, selected, onOpen, onDragStart, onDragEnd, dragging, compact = false, readOnly = false }) {
  const { task, span } = item;
  const done = isClosed(task);
  const overdue = !done && span.end < today;
  const movable = !done && !readOnly;
  return (
    <button
      type="button"
      draggable={movable}
      onDragStart={(event) => onDragStart(event, item)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(task)}
      title={`${task.name}${span.multi ? ` · ${formatDate(span.start, { year: 'auto' })} → ${formatDate(span.end, { year: 'auto' })}` : ''}${overdue ? ' · overdue' : ''}`}
      className={cn(
        'flex w-full min-w-0 items-start gap-1.5 rounded-lg border px-2 py-1.5 text-left text-[12px] font-semibold leading-[1.3] text-ink shadow-card transition-[box-shadow,border-color,opacity] hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        overdue ? 'border-status-red-bg bg-status-red-bg' : 'border-line bg-surface',
        done && 'bg-surface-2 text-ink-3 line-through decoration-ink-4 shadow-none',
        selected && 'border-[color:var(--brand-ink)] bg-brand-soft',
        dragging && 'opacity-40',
        movable && 'cursor-grab active:cursor-grabbing'
      )}
      data-testid={`calendar-task-${task.id}`}
    >
      <StatusIcon status={task.status} className="mt-px [&_svg]:h-[13px] [&_svg]:w-[13px]" />
      <span dir="auto" className={cn('min-w-0 flex-1 text-start', compact ? 'truncate' : 'line-clamp-2')}>{task.name}</span>
      {span.multi && compact ? <MoveHorizontal className="h-3 w-3 shrink-0 text-ink-3" aria-label="Multi-day" /> : null}
    </button>
  );
}

export default function TaskCalendar({
  tasks,
  doneTasks = [],
  mode = 'week',
  onModeChange,
  anchor,
  onAnchorChange,
  selectedTaskId,
  onOpen,
  onMoveDates,
  isPhone = false,
  readOnly = false,
}) {
  const today = todayIso();
  const base = anchor || today;
  const [drag, setDrag] = useState(null); // { taskId, fromDay }
  const [dropDay, setDropDay] = useState(null);

  const items = useMemo(() => [...tasks, ...doneTasks]
    .map((task) => ({ task, span: taskSpan(task) }))
    .filter((item) => item.span), [doneTasks, tasks]);
  const undated = tasks.filter((task) => !isClosed(task) && !taskSpan(task)).length;

  const effectiveMode = isPhone ? 'week' : mode;
  const days = effectiveMode === 'month' ? monthGrid(base) : weekDays(base);
  const weekStart = startOfWeekIso(base);
  const weekEnd = addDaysIso(weekStart, 6);

  /** Tasks that sit on a day: single-day ones on their due day; in Month, multi-day ones on each of their days. */
  const onDay = (day, { includeMulti = false } = {}) => items.filter(({ span }) => (
    includeMulti ? span.start <= day && span.end >= day : !span.multi && span.end === day
  ));

  const startDrag = (event, item) => {
    if (readOnly || isClosed(item.task)) return;
    const fromDay = item.span.multi && item.dragFrom ? item.dragFrom : item.span.end;
    setDrag({ taskId: item.task.id, fromDay });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(DRAG_TYPE, item.task.id);
    event.dataTransfer.setData('text/plain', item.task.name);
  };
  const endDrag = () => {
    setDrag(null);
    setDropDay(null);
  };
  const dropProps = (day) => ({
    onDragOver: (event) => {
      if (!drag) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dropDay !== day) setDropDay(day);
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget) && dropDay === day) setDropDay(null);
    },
    onDrop: (event) => {
      event.preventDefault();
      const current = drag;
      endDrag();
      if (!current) return;
      const item = items.find((entry) => entry.task.id === current.taskId);
      if (!item) return;
      const delta = daysBetweenIso(current.fromDay, day);
      if (!delta) return;
      const next = { due_date: addDaysIso(item.span.end, delta) };
      // A multi-day task keeps its length — also one whose start is its due day ("Make multi-day" on a task
      // without a date), whose start would otherwise end up after its new due date (the server refuses that).
      const start = item.task.is_multi_day ? isoDate(item.task.start_date) : '';
      if (start) next.start_date = addDaysIso(start, delta);
      onMoveDates(item.task, next, day);
    },
  });

  const shift = (direction) => {
    if (effectiveMode === 'month') {
      const [year, month] = base.split('-').map(Number);
      const target = new Date(Date.UTC(year, month - 1 + direction, 1));
      onAnchorChange(`${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, '0')}-01`);
    } else {
      onAnchorChange(addDaysIso(base, direction * 7));
    }
  };

  const title = effectiveMode === 'month'
    ? new Date(`${base.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : `Week of ${formatDate(weekStart, { year: 'auto' })}`;

  // Footer facts (week view)
  const overdueEarlier = items.filter(({ task, span }) => !isClosed(task) && span.end < weekStart && span.end < today);
  const nextWeekStart = addDaysIso(weekStart, 7);
  const nextWeekItems = items
    .filter(({ task, span }) => !isClosed(task) && span.end >= nextWeekStart && span.end <= addDaysIso(nextWeekStart, 6))
    .sort((a, b) => (a.span.end < b.span.end ? -1 : 1));

  const chipProps = (item) => ({
    item,
    today,
    selected: item.task.id === selectedTaskId,
    onOpen,
    onDragStart: startDrag,
    onDragEnd: endDrag,
    dragging: drag?.taskId === item.task.id,
    readOnly,
  });

  const header = (
    <header className="flex flex-wrap items-center gap-3 border-b border-line-2 px-4 py-3 sm:px-5">
      <span className="ui-icon-tile tone-navy h-9 w-9" aria-hidden="true"><CalendarDays className="h-[17px] w-[17px]" /></span>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-bold text-ink" data-testid="tasks-calendar-title">{title}</h2>
        <p className="text-[12.5px] text-ink-3 max-sm:hidden">
          Same tasks and filters as the board{readOnly ? '' : ' · drag a task to another day to change its due date'}
        </p>
      </div>
      {!isPhone ? (
        <Segmented
          ariaLabel="Calendar range"
          value={effectiveMode}
          onChange={onModeChange}
          options={[{ key: 'week', label: 'Week', testId: 'calendar-mode-week' }, { key: 'month', label: 'Month', testId: 'calendar-mode-month' }]}
          size="sm"
        />
      ) : null}
      <div className="flex items-center gap-1">
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => shift(-1)} aria-label={effectiveMode === 'month' ? 'Previous month' : 'Previous week'}>
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onAnchorChange('')} disabled={days.includes(today) && !anchor}>Today</Button>
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => shift(1)} aria-label={effectiveMode === 'month' ? 'Next month' : 'Next week'} data-testid="calendar-next">
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </header>
  );

  let body;
  if (isPhone) {
    body = (
      <ol className="divide-y divide-[color:var(--line-2)]" aria-label="Days of the week">
        {days.map((day) => {
          const dayItems = onDay(day, { includeMulti: true });
          const isToday = day === today;
          return (
            <li key={day} className={cn('flex flex-col gap-2 px-4 py-3', isToday && 'bg-brand-soft')}>
              <div className="flex items-baseline gap-2 text-[12.5px] font-bold text-ink-2">
                <span className="uppercase tracking-[0.05em]">{WEEKDAY[weekdayIndex(day)]}</span>
                <span className="text-ink">{formatDate(day, { year: 'auto' })}</span>
                {isToday ? <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-brand-ink">Today</span> : null}
              </div>
              {dayItems.length
                ? dayItems.map((item) => <TaskChip key={item.task.id} {...chipProps(item)} />)
                : <span className="text-[12.5px] text-ink-4">{weekdayIndex(day) >= 5 ? 'Weekend' : 'Nothing due'}</span>}
            </li>
          );
        })}
      </ol>
    );
  } else if (effectiveMode === 'month') {
    const month = base.slice(0, 7);
    body = (
      <div className="overflow-hidden">
        <div className="grid grid-cols-7 border-b border-line-2 bg-surface-2">
          {WEEKDAY.map((name) => <div key={name} className="px-2.5 py-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-3">{name}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const dayItems = onDay(day, { includeMulti: true }).map((item) => ({ ...item, dragFrom: day }));
            const outside = day.slice(0, 7) !== month;
            const isToday = day === today;
            return (
              <div
                key={day}
                {...dropProps(day)}
                className={cn(
                  'flex min-h-[112px] min-w-0 flex-col gap-1 border-b border-r border-line-2 p-1.5 [&:nth-child(7n)]:border-r-0',
                  outside && 'bg-surface-2',
                  weekdayIndex(day) >= 5 && !outside && 'bg-[color:var(--app-background)]',
                  dropDay === day && 'bg-brand-soft shadow-[inset_0_0_0_2px_var(--brand-soft-border)]'
                )}
                data-testid={`calendar-day-${day}`}
              >
                <span className={cn('grid h-6 w-6 place-items-center self-start rounded-full text-[12px] font-bold', outside ? 'text-ink-4' : 'text-ink-2', isToday && 'bg-brand text-brand-fg')}>
                  {dayNumber(day)}
                </span>
                {dayItems.slice(0, 3).map((item) => <TaskChip key={item.task.id} compact {...chipProps(item)} />)}
                {dayItems.length > 3 ? (
                  <button
                    type="button"
                    onClick={() => { onAnchorChange(day); onModeChange('week'); }}
                    className="self-start rounded px-1 text-[11.5px] font-semibold text-brand-ink hover:underline"
                  >
                    +{dayItems.length - 3} more
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    const bars = layoutBars(items, days).map((bar) => ({ ...bar, dragFrom: days[bar.startCol] }));
    const barRows = bars.reduce((max, bar) => Math.max(max, bar.row + 1), 0);
    // Task chips need the room; a multi-day bar runs across several columns and reads fine either way.
    const busyDays = new Set(days.filter((day) => onDay(day).length));
    const columns = { gridTemplateColumns: weekColumnTemplate(days, { today, busyDays }) };
    body = (
      <div className="relative">
        <div className="grid border-b border-line-2" style={columns} data-testid="calendar-week-head">
          {days.map((day) => {
            const isToday = day === today;
            return (
              <div key={day} className={cn('flex min-w-0 items-center gap-1.5 overflow-hidden border-r border-line-2 py-2.5 pl-3 pr-3.5 last:border-r-0', isToday && 'bg-brand-soft')}>
                <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-3">{WEEKDAY[weekdayIndex(day)]}</span>
                <span className={cn('grid h-7 min-w-[28px] place-items-center rounded-full px-1 font-heading text-[15px] font-extrabold text-ink', isToday && 'bg-brand text-brand-fg')}>
                  {dayNumber(day)}
                </span>
                {isToday ? <span className="ml-auto truncate text-[10.5px] font-bold uppercase tracking-[0.06em] text-brand-ink max-xl:hidden">Today</span> : null}
              </div>
            );
          })}
        </div>
        {barRows ? (
          <div className="grid gap-y-1 border-b border-line-2 px-1 py-1.5" style={{ ...columns, gridTemplateRows: `repeat(${barRows}, auto)` }}>
            {bars.map((bar) => {
              const done = isClosed(bar.task);
              const movable = !done && !readOnly;
              return (
                <button
                  key={bar.task.id}
                  type="button"
                  draggable={movable}
                  onDragStart={(event) => startDrag(event, bar)}
                  onDragEnd={endDrag}
                  onClick={() => onOpen(bar.task)}
                  style={{ gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`, gridRow: bar.row + 1 }}
                  title={`${bar.task.name} · ${formatDate(bar.span.start, { year: 'auto' })} → ${formatDate(bar.span.end, { year: 'auto' })}`}
                  className={cn(
                    'mx-1 flex h-7 min-w-0 items-center gap-1.5 border border-status-blue-bg bg-status-blue-bg px-2 text-left text-[12px] font-semibold text-status-blue-ink transition-[box-shadow,opacity] hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    bar.clippedStart ? 'rounded-l-none border-l-0' : 'rounded-l-lg',
                    bar.clippedEnd ? 'rounded-r-none border-r-0' : 'rounded-r-lg',
                    bar.task.id === selectedTaskId && 'border-[color:var(--brand-ink)]',
                    done && 'opacity-60 line-through',
                    drag?.taskId === bar.task.id && 'opacity-40',
                    movable && 'cursor-grab active:cursor-grabbing'
                  )}
                  data-testid={`calendar-bar-${bar.task.id}`}
                >
                  <StatusIcon status={bar.task.status} className="[&_svg]:h-[13px] [&_svg]:w-[13px]" />
                  <span dir="auto" className="min-w-0 flex-1 truncate text-start">{bar.task.name}</span>
                  <span className="shrink-0 font-medium text-ink-3 max-lg:hidden">
                    {formatDate(bar.span.start, { year: 'auto' })} – {formatDate(bar.span.end, { year: 'auto' })}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="grid" style={columns}>
          {days.map((day) => {
            const dayItems = onDay(day);
            const weekend = weekdayIndex(day) >= 5;
            const isDrop = dropDay === day;
            return (
              <div
                key={day}
                {...dropProps(day)}
                className={cn(
                  'flex min-h-[200px] min-w-0 flex-col gap-1.5 border-r border-line-2 p-1.5 transition-colors last:border-r-0',
                  weekend && 'bg-surface-2',
                  day === today && 'bg-brand-soft', // today stays marked on a weekend too
                  isDrop && 'bg-brand-soft shadow-[inset_0_0_0_2px_var(--brand-soft-border)]'
                )}
                data-testid={`calendar-day-${day}`}
              >
                {dayItems.map((item) => <TaskChip key={item.task.id} {...chipProps(item)} />)}
                {!dayItems.length && weekend && !isDrop ? <span className="m-auto text-[12px] font-semibold text-ink-4">Weekend</span> : null}
                {isDrop ? (
                  <span className="mt-auto inline-flex items-center gap-1 self-center rounded-md border border-dashed border-[color:var(--brand-soft-border)] px-2 py-1 text-[11.5px] font-semibold text-brand-ink">
                    Drop to set {formatDate(day, { year: 'auto' })}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const showFooter = effectiveMode === 'week' && (overdueEarlier.length || undated || nextWeekItems.length);
  return (
    <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card" aria-label="Calendar" data-testid="tasks-calendar">
      {header}
      {body}
      {showFooter ? (
        <footer className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line-2 px-4 py-3 text-[12.5px] text-ink-3 sm:px-5">
          {overdueEarlier.length ? (
            <span className="flex min-w-0 items-center gap-2">
              <StatusPill tone="red" icon={TriangleAlert} label={`${overdueEarlier.length} overdue from earlier`} size="sm" />
              <button type="button" onClick={() => onOpen(overdueEarlier[0].task)} className="min-w-0 truncate text-left font-semibold text-ink-2 hover:underline">
                <bdi>{overdueEarlier[0].task.name}</bdi> · {formatDate(overdueEarlier[0].span.end, { year: 'auto' })}
              </button>
            </span>
          ) : null}
          {undated ? <span>{undated} {undated === 1 ? 'task has' : 'tasks have'} no date (not on the calendar)</span> : null}
          <span className="flex-1" />
          {nextWeekItems.length ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>
                Next week · {nextWeekItems.length} {nextWeekItems.length === 1 ? 'task' : 'tasks'}
                {' '}({[...new Set(nextWeekItems.map((item) => formatDate(item.span.end, { year: 'auto' })))].slice(0, 4).join(', ')})
              </span>
              <button type="button" onClick={() => shift(1)} className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline">
                Next week <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}
